import { Controller, useForm } from "react-hook-form";
import { SectionBox } from "../../../components/sectionBox/SectionBox";
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  Flex,
  FormControl,
  HStack,
  Input,
  Stack,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FormLabel } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  GET_CAMPAIGN_BUNDLE_LIST,
  GET_ITEM_BUNDLE_CONTEXT,
  PROTECTED_GET_CAMPAIGN_INFO,
  UPDATE_BUNDLE_CONEXT,
} from "../../../graphql/operations/campaignOperations/operations";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { SelectFieldV2 } from "../../../components/selectField/SelectFieldV2";
import { formatTextDisplay } from "../../../utils/displayGetters";
import { IdDisplayBox } from "./IdDisplayBox";
import {
  LineTypeChoicesEnum,
  SegmentBundleType,
} from "../../../graphql/generated";
import { SegmentContextDisplay } from "./segmentDisplay";
import { ComponentLoading } from "../../../components/loading/ComponentLoading";
import { FiMinusCircle, FiPlusCircle } from "react-icons/fi";

export type tacticLevelDropdown = {
  label: string;
  value: LineTypeChoicesEnum;
};

interface BundleOptions {
  label: string;
  value: string;
  segment: string;
  deal: string;
}

export const SegmentInputDetails = (props: BoxProps) => {
  const { id: campaignId } = useParams();

  const [dsp, setDsp] = useState<"DV360" | "TTD" | "XANDR">("DV360");

  const [localTacticLevel, setLocalTacticLevel] = useState<tacticLevelDropdown>(
    { label: "", value: LineTypeChoicesEnum.LineItem }
  );
  const [localTacticLevelIds, setLocalTacticLevelIds] = useState<string[]>([]);

  const [localNonIllumaTacticLevelIds, setLocalNonIllumaTacticLevelIds] =
    useState<string[]>([]);
  const [tacticIdInput, setTacticIdInput] = useState("");
  const [tacticIdInputNonIlluma, setTacticIdInputNonIlluma] = useState("");
  const [addNonIllumaIds, setAddNonIllumaIds] = useState(false);

  const [localSegmentBundleIds, setLocalSegmentBundleIds] = useState<
    BundleOptions[]
  >([]);
  let segmentOptions: any[] = [];
  let dealOptions: any[] = [];
  const [createSegmentContext] = useMutation(UPDATE_BUNDLE_CONEXT);
  const [isSegmentAssigned, setIsSegmentAssigned] = useState(false);
  const [showSegmentOptions, setShowSegmentOptions] = useState(true);
  const [showDealOptions, setShowDealOptions] = useState(false);

  const tacticLevelOptions: tacticLevelDropdown[] = [
    ...Object.entries(lineTypeMapping(dsp)).map(([, value]) => ({
      label: formatTextDisplay(value),
      value: value,
    })),
  ];

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      illumaCampaign: "",
      defaultTacticLevel: null,
      segmentNames: [],
      dealNames: [],
      dspTacticLevel: null,
      dspTacticLevelId: "",
      dspNonIllumaTacticLevelId: "",
    },
  });

  // Fetch campaign info

  const { data, error } = useQuery(PROTECTED_GET_CAMPAIGN_INFO, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: campaignId,
    },
    onCompleted: () => {
      if (data?.campaignInfo?.name) {
        setValue("illumaCampaign", data.campaignInfo.name);
      }

      if (data?.campaignInfo?.dspInfo?.dspName) {
        setDsp(data.campaignInfo.dspInfo.dspName);
      }
    },
  });

  // Fetch segment bundle list

  const {
    data: bundleData,
    error: bundleError,
    loading: bundleLoading,
  } = useQuery(GET_CAMPAIGN_BUNDLE_LIST, {
    variables: { campaignId: campaignId },
    fetchPolicy: "network-only",
  });

  if (error || bundleError) {
    return <> Error </>;
  }

  // assign segment and deal options based on fetched bundle list and user selection of segment vs deal
  if (bundleData) {
    if (showSegmentOptions) {
      segmentOptions = bundleData.campaignBundleList
        // only show bundles with segments
        .filter(
          (bundle: SegmentBundleType) =>
            bundle.segment !== null && bundle.segment !== undefined
        )
        .map((bundle: SegmentBundleType) => ({
          label: `${bundle.segment.segmentId} - ${bundle.segment.name}`,
          value: bundle.id,
          segment: bundle.segment
            ? `${bundle.segment.segmentId} - ${bundle.segment.name}`
            : null,
          deal: bundle.deal
            ? `${bundle.deal?.dealId} - ${bundle.deal?.name}`
            : null,
        }));
    } else if (showDealOptions) {
      dealOptions = bundleData.campaignBundleList
        // only show bundles with deals
        .filter(
          (bundle: SegmentBundleType) =>
            bundle.deal !== null && bundle.deal !== undefined
        )
        .map((bundle: SegmentBundleType) => ({
          label: `${bundle.deal.dealId} - ${bundle.deal.name}`,
          value: bundle.id,
          segment: bundle.segment
            ? `${bundle.segment.segmentId} - ${bundle.segment.name}`
            : null,
          deal: `${bundle.deal.dealId} - ${bundle.deal.name}`,
        }));
    }
  }

  // Get assigned segment context for the campaign

  const [
    getItemBundleContext,
    { data: bundleContextData, loading: bundleContextLoading },
  ] = useLazyQuery(GET_ITEM_BUNDLE_CONTEXT, {
    variables: {
      campaignId: campaignId,
    },
  });

  useEffect(() => {
    if (campaignId) {
      getItemBundleContext();
    }
  }, [campaignId]);

  useEffect(() => {
    if (
      bundleContextData &&
      bundleContextData.segmentContextItem &&
      bundleContextData.segmentContextItem !== null &&
      bundleContextData.segmentContextItem.length > 0
    ) {
      setIsSegmentAssigned(true);
    }
  }, [bundleContextData]);

  if (bundleContextLoading) {
    return <ComponentLoading text={"Loading segment context..."} />;
  }

  const handleAddId = () => {
    const ids = tacticIdInput.split(",");
    ids.map((id) => {
      id = id.trim();
      if (id && !localTacticLevelIds.includes(id)) {
        setLocalTacticLevelIds((prevIds) => [...prevIds, id]);
      }
    });
  };

  const handleAddNonIllumaId = () => {
    const ids = tacticIdInputNonIlluma.split(",");
    ids.map((id) => {
      id = id.trim();
      if (id && !localNonIllumaTacticLevelIds.includes(id)) {
        setLocalNonIllumaTacticLevelIds((prevIds) => [...prevIds, id]);
      }
    });
  };

  const segmentBundleids = localSegmentBundleIds?.map((bundle) => bundle.value);

  const onSubmit = () => {
    createSegmentContext({
      variables: {
        campaignId: campaignId,
        dspTacticLevel: localTacticLevel?.value,
        dspTacticIds: localTacticLevelIds,
        segmentBundleIds: segmentBundleids,
        nonIllumaTacticIds: localNonIllumaTacticLevelIds,
      },
    })
      .then(() => {
        getItemBundleContext();
        reset();
        setLocalTacticLevelIds([]);
        setLocalSegmentBundleIds([]);
        setLocalNonIllumaTacticLevelIds([]);
        setValue("illumaCampaign", data.campaignInfo.name);
      })
      .catch((error) => error);
  };

  return (
    <>
      <SectionBox sectionHeader={`Segment Details`} {...props}>
        <>
          {bundleContextData &&
            bundleContextData.segmentContextItem &&
            bundleContextData?.segmentContextItem?.map(
              (context: any, index: number) => (
                <SegmentContextDisplay key={index} bundleContext={context} />
              )
            )}
        </>

        {isSegmentAssigned ? (
          <>
            <Button
              variant="blueButton"
              onClick={() => {
                setIsSegmentAssigned(false);
              }}
            >
              Add another segment +
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate id="segment-form">
            <FormControl isRequired>
              <FormLabel htmlFor="illumaCampaign">Illuma Campaign:</FormLabel>
              <Input
                variant="greyInput"
                id="illumaCampaign"
                {...register("illumaCampaign", { required: true })}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel mt={3}>Segment Bundle:</FormLabel>
              {errors.segmentNames && (
                <Text color={"red"}>Segment Id is required.</Text>
              )}
              <Text fontSize={"sm"} as="span">
                Find by{" "}
                <Button
                  mb={1}
                  fontSize={"sm"}
                  variant={"link"}
                  padding={0}
                  _hover={{
                    color: "primary.light",
                    borderColor: "primary.light",
                  }}
                  onClick={() => {
                    setShowSegmentOptions(true);
                    setShowDealOptions(false);
                  }}
                >
                  Segment
                </Button>{" "}
                or{""}
                <Button
                  mb={1}
                  fontSize={"sm"}
                  variant={"link"}
                  padding={0}
                  _hover={{
                    color: "primary.light",
                    borderColor: "primary.light",
                  }}
                  onClick={() => {
                    setShowDealOptions(true);
                    setShowSegmentOptions(false);
                  }}
                >
                  Deal
                </Button>
                {/* or{" "}
                <Button
                  mb={1}
                  fontSize={"sm"}
                  variant={"link"}
                  padding={0}
                  _hover={{
                    color: "primary.light",
                    borderColor: "primary.light",
                  }}
                  onClick={() => {
                    setShowDealOptions(false);
                    setShowSegmentOptions(false);
                  }}
                >
                  Bundle
                </Button> */}
              </Text>
              <>
                {showSegmentOptions && (
                  <Controller
                    control={control}
                    name="segmentNames"
                    rules={{
                      validate: () =>
                        showSegmentOptions
                          ? !!watch("segmentNames")?.length ||
                            "Segment is required."
                          : true,
                    }}
                    render={({ field }) => (
                      <SelectFieldV2
                        testId={"segmentNames"}
                        styles={{
                          valueContainer: (base) => ({
                            ...base,
                            backgroundColor: "white",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            backgroundColor: "white",
                          }),
                          menu: (base) => ({
                            ...base,
                            borderColor: "chakra.colors.theme.blueInputHover",
                            borderwidth: "2px",
                            borderRadius: "chakraTheme.radii.xs",
                          }),
                        }}
                        placeholder={"Select Segment Name"}
                        isMulti={true}
                        isLoading={bundleLoading}
                        value={field.value}
                        options={segmentOptions}
                        onChange={(option) => {
                          setLocalSegmentBundleIds(option);
                          field.onChange(option);
                        }}
                      />
                    )}
                  />
                )}
              </>
              <>
                {showDealOptions && (
                  <Controller
                    control={control}
                    name="dealNames"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SelectFieldV2
                        testId={"dealNames"}
                        styles={{
                          valueContainer: (base) => ({
                            ...base,
                            backgroundColor: "white",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            backgroundColor: "white",
                          }),
                          menu: (base) => ({
                            ...base,
                            borderColor: "chakra.colors.theme.blueInputHover",
                            borderwidth: "2px",
                            borderRadius: "chakraTheme.radii.xs",
                          }),
                        }}
                        placeholder={"Select Deal Name"}
                        isMulti={true}
                        isLoading={bundleLoading}
                        value={field.value}
                        options={dealOptions}
                        onChange={(option) => {
                          setLocalSegmentBundleIds(option);
                          field.onChange(option);
                        }}
                      />
                    )}
                  />
                )}
              </>
              <VStack align={"flex-start"}>
                {(showSegmentOptions || showDealOptions) &&
                  localSegmentBundleIds.map((bundle, index) => (
                    // <IdDisplayBox key={index} idListMap={{idList: bundle.value}} />
                    <Tag
                      key={index}
                      // borderWidth={"1px"}
                      borderRadius={"5px"}
                      backgroundColor={"blueInputHover"}
                      flexWrap={"wrap"}
                      mt={3}
                      px={3}
                      py={2}
                    >
                      <Stack direction="row" spacing={"6"}>
                        {/* <Box>
                          <Text
                            color={"primary.darker"}
                            fontWeight={"semibold"}
                            fontSize={"sm"}
                          >
                            Bundle:
                          </Text>
                          <Text
                            mt={2}
                            px={3}
                            py={2}
                            // borderRadius={"20px"}
                            // variant={"solid"}
                            // color={"text.dark"}
                            // backgroundColor={"primary.light"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {bundle.value}
                          </Text>
                        </Box> */}

                        <Box>
                          {bundle.segment && (
                            <>
                              <Text
                                color={"primary.darker"}
                                fontWeight={"semibold"}
                                fontSize={"sm"}
                                mr={2}
                                alignItems="center"
                                justifyContent="center"
                              >
                                Segment:
                              </Text>

                              <Tag
                                mt={2}
                                px={3}
                                py={2}
                                borderRadius={"20px"}
                                variant={"solid"}
                                backgroundColor={"tagBlue"}
                                color={"white"}
                                fontWeight={"semibold"}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {bundle.segment}
                              </Tag>
                            </>
                          )}
                        </Box>
                        <Box>
                          {bundle.deal && (
                            <>
                              <Text
                                color={"primary.darker"}
                                fontWeight={"semibold"}
                                fontSize={"sm"}
                                mr={2}
                              >
                                Deal:
                              </Text>
                              <Tag
                                mt={2}
                                px={3}
                                py={2}
                                borderRadius={"20px"}
                                variant={"solid"}
                                backgroundColor={"tagBlue"}
                                color={"white"}
                                fontWeight={"semibold"}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {bundle.deal}
                              </Tag>
                            </>
                          )}
                        </Box>
                      </Stack>
                    </Tag>
                  ))}
              </VStack>

              {/* {!showSegmentOptions && !showDealOptions && (
                <Controller
                  control={control}
                  name="segmentBundleIds"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SelectFieldV2
                      testId={"segmentBundleIds"}
                      styles={{
                        valueContainer: (base) => ({
                          ...base,
                          backgroundColor: "white",
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          backgroundColor: "white",
                        }),
                        menu: (base) => ({
                          ...base,
                          borderColor: "chakra.colors.theme.blueInputHover",
                          borderwidth: "2px",
                          borderRadius: "chakraTheme.radii.xs",
                        }),
                      }}
                      placeholder={"Select Bundle Ids"}
                      isMulti={true}
                      isLoading={bundleLoading}
                      value={field.value}
                      options={bundleOptions}
                      onChange={(option) => {
                        setLocalSegmentBundleIds(option);
                        field.onChange(option);
                      }}
                    />
                  )}
                />
              )} */}
            </FormControl>
            <FormControl isRequired>
              <FormLabel mt={3} htmlFor="dspTacticLevel">
                Dsp Tactic Level:
              </FormLabel>
              {errors.dspTacticLevel && (
                <Text color={"red"}>DSP Tactic Level is required.</Text>
              )}
              <Controller
                control={control}
                name={"dspTacticLevel"}
                rules={{
                  validate: (value) =>
                    value ? true : "DSP Tactic Level is required.",
                }}
                render={({ field }) => (
                  <SelectFieldV2
                    testId={"dspTacticLevel"}
                    styles={{
                      valueContainer: (base) => ({
                        ...base,
                        backgroundColor: "white",
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        backgroundColor: "white",
                      }),
                      menu: (base) => ({
                        ...base,
                        borderColor: "chakra.colors.theme.blueInputHover",
                        borderwidth: "2px",
                        borderRadius: "chakraTheme.radii.xs",
                      }),
                    }}
                    placeholder={"Select Tactic Level"}
                    isMulti={false}
                    value={field.value}
                    options={tacticLevelOptions}
                    onChange={(option) => {
                      setLocalTacticLevel(option);
                      field.onChange(option);
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel mt={3}>Dsp Illuma Tactic Level IDs:</FormLabel>
              {errors.dspTacticLevelId && (
                <Text color={"red"}> Tactic Level Ids are required.</Text>
              )}
              <Controller
                control={control}
                name={"dspTacticLevelId"}
                rules={{
                  validate: () => {
                    return localTacticLevelIds.length > 0
                      ? true
                      : "Tactic Level Ids are required.";
                  },
                }}
                render={({ field }) => (
                  <HStack>
                    <Input
                      variant="greyInput"
                      placeholder="Enter DSP Tactic Level ID"
                      value={field.value || ""}
                      onChange={(option) => {
                        setTacticIdInput(option.target.value);
                        field.onChange(option.target.value);
                      }}
                      data-testid="dsp-tactic-level-id-input"
                    />
                    <Button
                      variant="greyButton"
                      data-testid="dsp-tactic-id-add-button"
                      onClick={() => {
                        handleAddId();
                        field.onChange("");
                      }}
                    >
                      +
                    </Button>
                  </HStack>
                )}
              />
              {localTacticLevelIds.length > 0 && (
                <IdDisplayBox
                  idListMap={{
                    idList: localTacticLevelIds,
                    setIdList: setLocalTacticLevelIds,
                  }}
                />
              )}
            </FormControl>
            <Flex mt={2} align={"stretch"} flexDirection={"column"}>
              <Button
                alignSelf="flex-start"
                variant="collapseLink"
                onClick={() => setAddNonIllumaIds(!addNonIllumaIds)}
                rightIcon={
                  addNonIllumaIds ? <FiMinusCircle /> : <FiPlusCircle />
                }
              >
                {!addNonIllumaIds
                  ? "Add Non-Illuma Tactic Level IDs"
                  : "Hide Non-Illuma Tactic Level IDs"}
              </Button>

              <Collapse in={addNonIllumaIds}>
                <FormControl isRequired width={"100%"}>
                  <FormLabel mt={3}>Dsp Non-Illuma Tactic Level IDs:</FormLabel>
                  {errors.dspNonIllumaTacticLevelId && (
                    <Text color={"red"}> Tactic Level Ids are required.</Text>
                  )}
                  <Controller
                    control={control}
                    name={"dspNonIllumaTacticLevelId"}
                    rules={{
                      validate: () => {
                        if (addNonIllumaIds) {
                          return localNonIllumaTacticLevelIds.length > 0
                            ? true
                            : "Tactic Level Ids are required.";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <HStack alignSelf="stretch">
                        <Input
                          variant="greyInput"
                          placeholder="Enter Non-Illuma DSP Tactic Level ID"
                          value={field.value || ""}
                          onChange={(option) => {
                            setTacticIdInputNonIlluma(option.target.value);
                            field.onChange(option.target.value);
                          }}
                          data-testid="dsp-non-illuma-tactic-level-id-input"
                        />
                        <Button
                          variant="greyButton"
                          data-testid="dsp-non-illuma-tactic-id-add-button"
                          onClick={() => {
                            handleAddNonIllumaId();
                            field.onChange("");
                          }}
                        >
                          +
                        </Button>
                      </HStack>
                    )}
                  />
                  {localNonIllumaTacticLevelIds.length > 0 && (
                    <IdDisplayBox
                      idListMap={{
                        idList: localNonIllumaTacticLevelIds,
                        setIdList: setLocalNonIllumaTacticLevelIds,
                      }}
                    />
                  )}
                </FormControl>
              </Collapse>
            </Flex>

            <Button
              type="submit"
              mt={4}
              variant="blueButton"
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </Button>
          </form>
        )}
      </SectionBox>
    </>
  );
};

const lineTypeMapping = (
  dsp: "DV360" | "TTD" | "XANDR" | "VERIZON_MEDIA"
): LineTypeChoicesEnum[] => {
  const mapping: Record<
    "DV360" | "TTD" | "XANDR" | "VERIZON_MEDIA",
    LineTypeChoicesEnum[]
  > = {
    DV360: [
      LineTypeChoicesEnum.Campaign,
      LineTypeChoicesEnum.Order,
      LineTypeChoicesEnum.LineItem,
    ],
    VERIZON_MEDIA: [
      LineTypeChoicesEnum.Campaign,
      LineTypeChoicesEnum.Order,
      LineTypeChoicesEnum.LineItem,
    ],
    TTD: [LineTypeChoicesEnum.Campaign, LineTypeChoicesEnum.Adgroup],
    XANDR: [
      LineTypeChoicesEnum.Order,
      LineTypeChoicesEnum.LineItem,
      LineTypeChoicesEnum.Split,
    ],
  };
  return mapping[dsp] || [LineTypeChoicesEnum.LineItem];
};
